import Select from "react-select";
import { FormField } from "../../ui-form/form-field";
import { Input } from "@/components/ui/input";
import type {
  FieldConfig,
  SelectFieldConfig,
  DateFieldConfig,
  TextFieldConfig,
  NumberFieldConfig,
  ConfigurableFormProps,
} from "./config";
import { FieldValues } from "react-hook-form";

export function renderField(field: FieldConfig, formField: any) {
  switch (field.type) {
    case "select": {
      const selectField = field as SelectFieldConfig;
      const isProductType = field.name === "productType";

      const options = isProductType
        ? selectField.options.map((opt) => ({
            ...opt,
            uniqueValue: `${opt.value}-${opt.label}`,
          }))
        : selectField.options;

      const getCurrentValue = () => {
        if (selectField.isMulti) {
          return options.filter((option) =>
            formField.value?.includes?.(
              isProductType ? option?.uniqueValue : option.value
            )
          );
        }

        return options.find((option) =>
          isProductType
            ? option?.uniqueValue === formField.value
            : option.value === formField.value
        );
      };

      return (
        <Select
          options={options}
          value={getCurrentValue()}
          onChange={(option: any) => {
            if (selectField.isMulti) {
              const values = Array.isArray(option)
                ? option.map((opt) =>
                    isProductType ? opt.uniqueValue : opt.value
                  )
                : [];
              formField.onChange(values);
            } else if (selectField?.valueType === "single_string") {
              formField.onChange(
                option
                  ? isProductType
                    ? option.uniqueValue
                    : option.value
                  : ""
              );
            } else {
              formField.onChange(option ? option : "");
            }
          }}
          getOptionValue={(opt) =>
            isProductType ? opt.uniqueValue : opt.value
          }
          getOptionLabel={(opt) => opt.label}
          placeholder={field.placeholder}
          className="react-select-container"
          classNamePrefix="react-select"
          isMulti={selectField.isMulti}
          components={{ IndicatorSeparator: () => null }}
          styles={{
            control: (base, state) => ({
              ...base,
              padding: "8px",
              borderRadius: "8px",
              borderColor: state.isFocused ? "#3b82f6" : base.borderColor,
              boxShadow: state.isFocused ? "0 0 0 1px #3b82f6" : base.boxShadow,
              "&:hover": {
                borderColor: "#3b82f6",
              },
            }),
            valueContainer: (base) => ({
              ...base,
              padding: "0 8px",
            }),
            singleValue: (base) => ({
              ...base,
              fontSize: "14px",
              fontWeight: 500,
              color: "#165FE3",
            }),
          }}
        />
      );
    }

    case "date": {
      const dateField = field as DateFieldConfig;
      return (
        <input
          type="date"
          min={dateField.min || new Date().toISOString().split("T")[0]}
          max={dateField.max}
          {...formField}
          placeholder={field.placeholder}
          className="w-full mt-2 p-4 text-[14px] text-[#666666] border border-gray-300 rounded-[8px] focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition duration-150"
        />
      );
    }

    case "number": {
      const numberField = field as NumberFieldConfig;
      return (
        <input
          type="number"
          min={numberField.min}
          max={numberField.max}
          step={numberField.step}
          {...formField}
          placeholder={field.placeholder}
          className="w-full mt-2 p-4 text-[14px] text-[#666666] border border-gray-300 rounded-[8px] focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition duration-150"
        />
      );
    }

    default: {
      const textField = field as TextFieldConfig;
      return (
        <input
          type={textField.type}
          {...formField}
          placeholder={field.placeholder}
          className="w-full mt-2 p-4 text-[14px] text-[#666666] border border-gray-300 rounded-[8px] focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition duration-150"
        />
      );
    }
  }
}

export function ConfigurableForm<T extends FieldValues = FieldValues>({
  form,
  fields,
  className = "space-y-6 max-w-sm",
}: ConfigurableFormProps<T>) {
  return (
    <div className={className}>
      {fields.map((field) => (
        <FormField
          key={field.name}
          form={form}
          name={field.name as Path<T>}
          label={field.label}
          render={({ field: formField }) => renderField(field, formField)}
        />
      ))}
    </div>
  );
}
